import {SupportedAsset} from 'src/types';
import CURRENCIES from '@stripe-internal/currencies';

/**
 * Apply Bridge rounding to an amount in minor currency units (e.g. USD cents)
 * so the merchant receives the full intended amount after Bridge deducts its
 * 10 bps exchange fee on USDT deposits.
 *
 * Formula (from Bridge docs): Input = Output / (1 - fee), rounded up to the
 * nearest cent.  Implemented with integer arithmetic to avoid floating-point
 * precision issues: ceil(amount * 1000 / 999).
 *
 * Only call this for USDT – other stablecoins (USDC, USDP, …) are pegged 1:1
 * and don't need adjustment.
 */
export const applyBridgeRounding = (amountMinor: number): number => {
  return Math.ceil((amountMinor * 1000) / 999);
};

/**
 * Returns true when the given asset code represents USDT, which requires
 * Bridge rounding adjustment.
 */
export const isUsdtAssetCode = (assetCode: string): boolean =>
  assetCode.toLowerCase() === 'usdt';

export const getDepositAmount = (amount: number, assetCode: string): number =>
  isUsdtAssetCode(assetCode) ? applyBridgeRounding(amount) : amount;

export const getQuotedAssetCode = (assetCode: string): string =>
  isUsdtAssetCode(assetCode) ? 'usd' : assetCode;

export const formatAmountToDollars = (amount: number) =>
  (amount / 100).toFixed(2);

export const getCurrencyDecimals = (currency: string) => {
  const currencyData = CURRENCIES[currency];
  if (!currencyData) {
    throw new Error(`Currency ${currency} not found`);
  }
  return Math.log10(currencyData.subunit_to_unit);
};

export const apiAmountToAssetAmount = (
  amount: number,
  sourceCurrency: string,
  minorDecimals: number,
): bigint => {
  const sourceDecimals = getCurrencyDecimals(sourceCurrency);
  const power = minorDecimals - sourceDecimals;
  if (power >= 0) {
    return BigInt(amount) * BigInt(10 ** power);
  }
  return BigInt(amount) / BigInt(10 ** -power);
};

export const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const getDisplayableAssetTitle = (asset: SupportedAsset) => {
  return asset.asset_code.toUpperCase();
};

export const redactWalletAddress = (address: string) =>
  `${address.substring(0, 2)} ···· ${address.substring(
    address.length - 4,
    address.length,
  )}`;

export const formatCurrencyAmountToMajorUnits = (
  amount: number,
  currency: string,
) => {
  const currencyData = CURRENCIES[currency];
  if (!currencyData) {
    throw new Error(`Currency ${currency} not found`);
  }
  return amount / currencyData.subunit_to_unit;
};

export const formatCurrencyAmountToMajorUnitsFixed = (
  amount: number,
  currency: string,
) => {
  const currencyData = CURRENCIES[currency];
  if (!currencyData) {
    throw new Error(`Currency ${currency} not found`);
  }
  return (amount / currencyData.subunit_to_unit).toFixed(2);
};
